import { useState } from "react";
import { coverSrc } from "../lib/images";

// When artwork is missing (or fails to load) we set a typographic plate instead
// of a stock photo, so the shelf never shows the same borrowed image twice.
const FallbackPlate = ({ title, creator, year, testId }) => (
  <span
    data-testid={testId ? `${testId}-plate` : undefined}
    className="absolute inset-0 flex flex-col justify-between bg-ink p-3 sm:p-4 overflow-hidden"
  >
    <span aria-hidden="true" className="absolute inset-1 border border-line/70" />
    <span className="relative font-mono text-[9px] uppercase tracking-[0.2em] text-vellum-faint">
      {Number.isFinite(year) ? (year < 0 ? `${Math.abs(year)} BCE` : year) : "No cover"}
    </span>
    <span className="relative mt-auto font-serif font-light leading-[0.95] tracking-tight text-vellum text-[clamp(1.05rem,3.1vw,2.5rem)] line-clamp-4 pb-4">
      {title}
    </span>
    <span className="relative font-mono text-[9px] uppercase tracking-[0.2em] text-brass truncate">
      {creator || "\u00A0"}
    </span>
  </span>
);

export default function CoverImage({
  src, alt, className = "", testId, focal, size = "thumb", priority = false,
  title, creator, year,
}) {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  if (error || !src) {
    return <FallbackPlate title={title || alt} creator={creator} year={year} testId={testId} />;
  }

  const style = {
    objectPosition: `${focal?.x ?? 50}% ${focal?.y ?? 50}%`,
    "--cover-flip": focal?.flip ? -1 : 1,
    opacity: loaded ? 1 : 0,
  };

  return (
    <>
      {!loaded && (
        <span
          aria-hidden="true"
          className="absolute inset-0 hatch"
          data-testid={testId ? `${testId}-skeleton` : undefined}
        />
      )}
      <img
        data-testid={testId}
        src={coverSrc(src, size)}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={priority ? "high" : "auto"}
        onError={() => setError(true)}
        onLoad={() => setLoaded(true)}
        style={style}
        className={`cover-img ${className}`}
      />
    </>
  );
}
